import React, { useEffect, useState, useCallback } from "react";
import API from "../services/api";
import { toast } from "react-toastify";
import {
  Calendar, Wallet, PlusCircle, RefreshCw, Lock, Unlock,
  ChevronRight, X, Save, TrendingUp, MapPin,
} from "lucide-react";

const STATUS_STYLES = {
  DRAFT:       "bg-gray-100 text-gray-600 border-gray-200",
  OPEN:        "bg-green-100 text-green-700 border-green-200",
  CLOSED:      "bg-red-100 text-red-700 border-red-200",
  DISBURSING:  "bg-blue-100 text-blue-700 border-blue-200",
  COMPLETED:   "bg-purple-100 text-purple-700 border-purple-200",
};

const formatKES = (amt) =>
  new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", maximumFractionDigits: 0 }).format(amt || 0);

/* ============================ CREATE CYCLE MODAL ============================ */
const CreateCycleModal = ({ isOpen, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "", academicYear: "", totalBudget: "",
    applicationOpenDate: "", applicationDeadline: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  if (!isOpen) return null;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name || !form.academicYear || !form.totalBudget || !form.applicationOpenDate || !form.applicationDeadline) {
      toast.error("Please fill all required fields");
      return;
    }
    setSaving(true);
    try {
      await API.post("/cycles", form);
      toast.success("Bursary cycle created. Ward budgets auto-distributed equally — adjust as needed.");
      onCreated();
      onClose();
      setForm({ name: "", academicYear: "", totalBudget: "", applicationOpenDate: "", applicationDeadline: "", notes: "" });
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create cycle");
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-lg w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">New Bursary Cycle</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelClass}>Cycle Name <span className="text-red-500">*</span></label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. 2024/2025 Academic Year Bursary" className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Academic Year <span className="text-red-500">*</span></label>
            <input name="academicYear" value={form.academicYear} onChange={handleChange} placeholder="e.g. 2024/2025" className={inputClass} />
          </div>

          <div>
            <label className={labelClass}>Total Budget (KES) <span className="text-red-500">*</span></label>
            <input type="number" name="totalBudget" value={form.totalBudget} onChange={handleChange} placeholder="e.g. 5000000" className={inputClass} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Opens <span className="text-red-500">*</span></label>
              <input type="date" name="applicationOpenDate" value={form.applicationOpenDate} onChange={handleChange} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Deadline <span className="text-red-500">*</span></label>
              <input type="date" name="applicationDeadline" value={form.applicationDeadline} onChange={handleChange} className={inputClass} />
            </div>
          </div>

          <div>
            <label className={labelClass}>Notes</label>
            <textarea name="notes" value={form.notes} onChange={handleChange} rows={2} placeholder="Optional internal notes" className={inputClass} />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white hover:bg-blue-800 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            Create Cycle
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================ WARD ALLOCATION EDITOR ============================ */
const WardAllocationEditor = ({ cycle, onClose, onSaved }) => {
  const [allocations, setAllocations] = useState(
    cycle.wardAllocations.map((w) => ({
      wardId: w.ward._id,
      wardName: w.ward.name,
      allocatedAmount: w.allocatedAmount,
      disbursedAmount: w.disbursedAmount,
    }))
  );
  const [saving, setSaving] = useState(false);

  const total = allocations.reduce((sum, a) => sum + Number(a.allocatedAmount || 0), 0);
  const overBudget = total > cycle.totalBudget;

  const handleAmountChange = (wardId, value) => {
    setAllocations((prev) =>
      prev.map((a) => (a.wardId === wardId ? { ...a, allocatedAmount: value } : a))
    );
  };

  const handleSave = async () => {
    if (overBudget) {
      toast.error("Total allocation exceeds the cycle budget");
      return;
    }
    setSaving(true);
    try {
      await API.put(`/cycles/${cycle._id}/ward-allocations`, {
        wardAllocations: allocations.map((a) => ({ wardId: a.wardId, allocatedAmount: a.allocatedAmount })),
      });
      toast.success("Ward allocations updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update allocations");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-2xl w-full border border-gray-100 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-bold text-gray-900">Ward Budget Allocation</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-5">{cycle.name} &middot; Total Budget: {formatKES(cycle.totalBudget)}</p>

        <div className="space-y-2.5 mb-4">
          {allocations.map((a) => (
            <div key={a.wardId} className="flex items-center gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <MapPin size={14} className="text-gray-400 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">{a.wardName}</span>
              </div>
              {a.disbursedAmount > 0 && (
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatKES(a.disbursedAmount)} disbursed
                </span>
              )}
              <input
                type="number"
                value={a.allocatedAmount}
                onChange={(e) => handleAmountChange(a.wardId, e.target.value)}
                className="w-36 border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm text-right focus:outline-none focus:border-primary flex-shrink-0"
              />
            </div>
          ))}
        </div>

        <div className={`flex justify-between items-center p-3 rounded-xl border-2 ${overBudget ? "bg-red-50 border-red-200" : "bg-blue-50 border-blue-200"}`}>
          <span className="text-sm font-medium text-gray-700">Total Allocated</span>
          <span className={`text-sm font-bold ${overBudget ? "text-red-600" : "text-primary"}`}>
            {formatKES(total)} / {formatKES(cycle.totalBudget)}
          </span>
        </div>
        {overBudget && (
          <p className="text-xs text-red-500 mt-2">⚠ Total exceeds the cycle's overall budget. Reduce allocations before saving.</p>
        )}

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-xl border-2 border-gray-200 hover:bg-gray-50 text-sm font-medium transition">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || overBudget}
            className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-white hover:bg-blue-800 text-sm font-semibold transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
            Save Allocations
          </button>
        </div>
      </div>
    </div>
  );
};

/* ============================ BUDGET SUMMARY PANEL ============================ */
const BudgetSummaryPanel = ({ cycleId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cycleId) return;
    setLoading(true);
    API.get(`/cycles/${cycleId}/budget-summary`)
      .then((res) => setSummary(res.data))
      .catch(() => toast.error("Failed to load budget summary"))
      .finally(() => setLoading(false));
  }, [cycleId]);

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!summary) return null;

  const utilizationPct = summary.totalBudget > 0
    ? Math.round((summary.totalDisbursed / summary.totalBudget) * 100)
    : 0;

  return (
    <div className="space-y-5">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs text-blue-600 uppercase tracking-wide mb-1">Total Budget</p>
          <p className="text-xl font-bold text-blue-900">{formatKES(summary.totalBudget)}</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-xl p-4">
          <p className="text-xs text-green-600 uppercase tracking-wide mb-1">Disbursed</p>
          <p className="text-xl font-bold text-green-900">{formatKES(summary.totalDisbursed)}</p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Remaining</p>
          <p className="text-xl font-bold text-gray-800">{formatKES(summary.remainingBudget)}</p>
        </div>
      </div>

      <div>
        <div className="flex justify-between text-xs text-gray-500 mb-1.5">
          <span>Budget Utilization</span>
          <span>{utilizationPct}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700"
            style={{ width: `${Math.min(utilizationPct, 100)}%` }}
          />
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
          <MapPin size={14} /> Ward Breakdown
        </h4>
        <div className="overflow-auto rounded-xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase">Ward</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-400 uppercase">Allocated</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-400 uppercase">Disbursed</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-400 uppercase">Apps</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {summary.wardBreakdown.map((w) => (
                <tr key={w.ward}>
                  <td className="px-3 py-2.5 font-medium text-gray-700">{w.ward}</td>
                  <td className="px-3 py-2.5 text-right text-gray-600">{formatKES(w.allocatedAmount)}</td>
                  <td className="px-3 py-2.5 text-right text-green-600">{formatKES(w.disbursedAmount)}</td>
                  <td className="px-3 py-2.5 text-right text-gray-500">{w.approved}/{w.applications}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ============================ MAIN PAGE ============================ */
const BudgetCyclePage = () => {
  const [cycles, setCycles]               = useState([]);
  const [loading, setLoading]             = useState(true);
  const [createOpen, setCreateOpen]       = useState(false);
  const [allocationCycle, setAllocationCycle] = useState(null);
  const [expandedCycle, setExpandedCycle] = useState(null);

  const fetchCycles = useCallback(async () => {
    try {
      setLoading(true);
      const res = await API.get("/cycles");
      setCycles(res.data);
    } catch {
      toast.error("Failed to load bursary cycles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCycles(); }, [fetchCycles]);

  const handleOpenCycle = async (id) => {
    if (!window.confirm("Open this cycle for student applications? Any other open cycle will be closed.")) return;
    try {
      await API.post(`/cycles/${id}/open`);
      toast.success("Cycle opened for applications");
      fetchCycles();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to open cycle");
    }
  };

  const handleCloseCycle = async (id) => {
    if (!window.confirm("Close this cycle? Students will no longer be able to submit applications.")) return;
    try {
      await API.post(`/cycles/${id}/close`);
      toast.success("Cycle closed");
      fetchCycles();
    } catch {
      toast.error("Failed to close cycle");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-primary">Budget & Bursary Cycles</h2>
          <p className="text-gray-500 text-sm mt-1">Manage academic year cycles, ward budgets, and application windows</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchCycles} className="bg-secondary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-700 active:scale-95 transition">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={() => setCreateOpen(true)} className="bg-primary text-white px-4 py-2 rounded-xl flex gap-2 items-center text-sm hover:bg-blue-800 active:scale-95 transition">
            <PlusCircle size={14} /> New Cycle
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-14 gap-3">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-400 text-sm">Loading cycles...</p>
        </div>
      ) : cycles.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-14 text-center">
          <Calendar size={40} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No bursary cycles yet</p>
          <p className="text-gray-400 text-sm mt-1">Create your first cycle to start accepting applications.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {cycles.map((cycle) => {
            const isExpanded = expandedCycle === cycle._id;
            return (
              <div key={cycle._id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-5 flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <Wallet size={20} className="text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-800 truncate">{cycle.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[cycle.status]}`}>
                          {cycle.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {cycle.academicYear} &middot; Budget {formatKES(cycle.totalBudget)} &middot; Deadline{" "}
                        {new Date(cycle.applicationDeadline).toLocaleDateString("en-KE")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setAllocationCycle(cycle)}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium transition flex items-center gap-1.5"
                    >
                      <TrendingUp size={12} /> Edit Allocations
                    </button>

                    {cycle.status === "DRAFT" && (
                      <button
                        onClick={() => handleOpenCycle(cycle._id)}
                        className="px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium transition flex items-center gap-1.5"
                      >
                        <Unlock size={12} /> Open Cycle
                      </button>
                    )}
                    {cycle.status === "OPEN" && (
                      <button
                        onClick={() => handleCloseCycle(cycle._id)}
                        className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition flex items-center gap-1.5"
                      >
                        <Lock size={12} /> Close Cycle
                      </button>
                    )}

                    <button
                      onClick={() => setExpandedCycle(isExpanded ? null : cycle._id)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-500 transition"
                    >
                      <ChevronRight size={16} className={`transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-100 p-5 bg-gray-50/50">
                    <BudgetSummaryPanel cycleId={cycle._id} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <CreateCycleModal isOpen={createOpen} onClose={() => setCreateOpen(false)} onCreated={fetchCycles} />

      {allocationCycle && (
        <WardAllocationEditor
          cycle={allocationCycle}
          onClose={() => setAllocationCycle(null)}
          onSaved={fetchCycles}
        />
      )}
    </div>
  );
};

export default BudgetCyclePage;
